'use client'

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { getApiBaseUrl } from '@/lib/config'
import { normalizeMarketPayload, type MarketPayload } from '@/lib/market-rates'
import { isLabPerformanceAgent, whenThirdPartyAllowed } from '@/lib/defer-third-party'

export type MarketRatesContextValue = {
  market: MarketPayload | null
  loading: boolean
  busy: boolean
  /** Canlı SSE aboneliği — strip mount olunca acquire, unmount’ta release */
  acquireLive: () => void
  releaseLive: () => void
}

export const MarketRatesContext = createContext<MarketRatesContextValue | null>(null)

function isDocumentVisible() {
  if (typeof document === 'undefined') return true
  return document.visibilityState !== 'hidden'
}

export function MarketRatesProvider({ children }: { children: ReactNode }) {
  const apiBase = useMemo(() => getApiBaseUrl(), [])
  const [market, setMarket] = useState<MarketPayload | null>(null)
  const [loading, setLoading] = useState(true)
  const [liveWanted, setLiveWanted] = useState(0)
  const liveCountRef = useRef(0)
  const esRef = useRef<EventSource | null>(null)

  const acquireLive = useCallback(() => {
    liveCountRef.current += 1
    setLiveWanted(liveCountRef.current)
  }, [])

  const releaseLive = useCallback(() => {
    liveCountRef.current = Math.max(0, liveCountRef.current - 1)
    setLiveWanted(liveCountRef.current)
  }, [])

  useEffect(() => {
    const shouldConnect = liveWanted > 0 && isDocumentVisible()
    if (!shouldConnect) {
      esRef.current?.close()
      esRef.current = null
      if (liveWanted === 0 && !market) setLoading(false)
      return
    }

    if (typeof EventSource === 'undefined') {
      setLoading(false)
      return
    }

    let cancelled = false
    let es: EventSource | null = null
    let liveUnlocked = false
    const abortController = new AbortController()

    const loadSnapshot = async () => {
      try {
        const response = await fetch(`${apiBase}/api/market/latest`, {
          cache: 'no-store',
          signal: abortController.signal,
        })
        if (!response.ok) return
        const json = await response.json()
        if (cancelled) return
        const next = normalizeMarketPayload(json?.data)
        if (next) setMarket(next)
      } catch {
        // SSE veya sonraki yenileme tekrar deneyebilir.
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    const open = () => {
      if (cancelled || !isDocumentVisible() || liveCountRef.current <= 0) return
      esRef.current?.close()
      es = new EventSource(`${apiBase}/api/market/stream`)
      esRef.current = es
      es.onmessage = (ev) => {
        try {
          const json = JSON.parse(ev.data)
          const next = normalizeMarketPayload(json?.data)
          if (next) setMarket(next)
          setLoading(false)
        } catch {
          // ignore malformed SSE
        }
      }
      es.onerror = () => {
        // Browser auto-reconnects via retry; keep loading false if we already have data
        if (!market) {
          // leave loading until first message or close
        }
      }
    }

    // İlk veri kısa HTTP isteğiyle gelsin. Uzun yaşayan SSE, ilk boya sonrası
    // açılır; Lighthouse/headless botlarında hiç açılmaz.
    void loadSnapshot()
    if (!isLabPerformanceAgent()) {
      void whenThirdPartyAllowed({ fallbackMs: 5_500 }).then(() => {
        if (cancelled) return
        liveUnlocked = true
        open()
      })
    }

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        esRef.current?.close()
        esRef.current = null
        es = null
      } else if (liveUnlocked && liveCountRef.current > 0) {
        open()
      }
    }
    const onOnline = () => {
      if (liveUnlocked && liveCountRef.current > 0 && isDocumentVisible()) open()
    }

    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('online', onOnline)

    return () => {
      cancelled = true
      abortController.abort()
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('online', onOnline)
      es?.close()
      if (esRef.current === es) esRef.current = null
    }
    // market intentionally omitted — onerror closure uses latest via setState
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase, liveWanted])

  const value = useMemo(
    () => ({
      market,
      loading: liveWanted > 0 ? loading : false,
      busy: liveWanted > 0 ? loading : false,
      acquireLive,
      releaseLive,
    }),
    [market, loading, liveWanted, acquireLive, releaseLive],
  )

  return <MarketRatesContext.Provider value={value}>{children}</MarketRatesContext.Provider>
}

/** Provider altında canlı abonelik; strip / kart mount’unda kullan. */
export function useMarketLiveSubscription(live: boolean) {
  const ctx = useContext(MarketRatesContext)
  const acquire = ctx?.acquireLive
  const release = ctx?.releaseLive
  useEffect(() => {
    if (!live || !acquire || !release) return

    acquire()
    return () => {
      release()
    }
  }, [live, acquire, release])
}
